import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock, Check, ChevronRight, ChevronDown, Sparkles, Heart, Sun, Droplet, Wind, Eye, Flame, Star, Calendar, Video, ExternalLink, PlayCircle, UtensilsCrossed } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useActivityTracker } from '../hooks/useActivityTracker';
import InquiryFormModal from '../components/InquiryFormModal';
import FaqAccordion from '../components/FaqAccordion';
import InfoSessionButton from '../components/InfoSessionButton';
import MealMap21 from '../components/MealMap21';
import ReflectionJournal from '../components/ReflectionJournal';
import FinalReflections from '../components/FinalReflections';
import FlexCommentsBoard from '../components/FlexCommentsBoard';
import { detoxFoodOverview as detoxFoodOverviewData } from '../data/detoxFoods';


// ============ TIERS ============
const flexDetoxFaqs = [
  {
    q: 'Is the Flex Detox program in-person?',
    a: 'Yes — it\'s an in-person program based at Breathing Room Bermuda, with weekly meet-up sessions to keep you anchored. The recipes, food lists, daily affirmations, and self-guided fascia practice are yours to follow at home throughout the 21 days.',
  },
  {
    q: 'What\'s the difference between the three tiers?',
    a: 'The Glow Up is the full 21-day color journey — foundations, fascia practice, themed recipes, shopping guides, and weekly meet-ups. Deep Detox layers in Breathing Room\'s frequency services (ionic foot detoxes, biofield scans, PEMF / red light). Full Immersion is the deepest support — more services, two private consult sessions with Jill, supportive services of your choice, and full access to in-person classes.',
  },
  {
    q: 'Do I need any experience or a certain fitness level?',
    a: 'Not at all. The journey meets your body exactly where it is. Guided questionnaires help us tailor the path to you — so the experience adjusts to your body, not the other way around.',
  },
  {
    q: 'How long is the program?',
    a: 'It runs 21 days, organized into seven color-aligned "systems" (three days each, from The Root to The Spirit). There\'s also an optional Week 4 "Living in the Full Spectrum" bonus if you\'d like to keep the momentum going.',
  },
  {
    q: 'Is this a restrictive cleanse or a fast?',
    a: 'No. There is plenty of food and you will not feel deprived — joy is a huge part of this process. It\'s about nourishing your body with color and using food as medicine, not depriving yourself.',
  },
  {
    q: 'What if I have dietary restrictions or allergies?',
    a: 'The food lists are flexible and built around whole, colorful foods, so they adapt well. Let Jill know your restrictions when you enroll and we\'ll make sure your journey works for your body.',
  },
  {
    q: 'How do I pick the right tier?',
    a: 'Reach out and Jill will help you find the right depth based on your goals, any symptoms or patterns you\'re navigating, and how much hands-on support you\'d like. You can always upgrade along the way.',
  },
  {
    q: 'How do I enroll?',
    a: 'Tap "Inquire about Enrollment" and share a few details. Jill will be in touch with current availability and the next steps. Spaces are limited.',
  },
];

const tiers = [
  {
    id: 1,
    slug: 'glow-up',
    name: 'The Glow Up',
    tagline: 'Foundations + Fascia',
    icon: '🌿',
    accent: 'from-emerald-200 to-green-100',
    accentText: 'text-emerald-700',
    features: [
      'Everything you need to support your journey',
      'Weekly meet-up sessions with frequency support',
      'Guided journey with questionnaires',
      'Themed recipes & shopping guides',
      'Daily affirmations & food lists',
      'Self-guided fascia practice',
    ],
    summary: [
      'Everything you need to support your journey, beautifully laid out. The Glow Up gives you the full 21-day color journey, themed recipes, shopping guides, daily affirmations, and self-guided fascia practice — with weekly meet-up sessions that include frequency support to keep you anchored along the way.',
      'You\'ll also receive guided questionnaires designed to help you find YOUR best path within the program — so the experience adjusts to your body, not the other way around.',
      'Best for: anyone wanting the full system experience with steady weekly touchpoints, but who\'s ready to do most of the practice at home.',
    ],
    price: '$125',
    priceBlurb: 'Your complete 21-day color journey, start to finish. Ready to go deeper? Add The Double Down as an optional power-up.',
  },
  {
    id: 2,
    slug: 'deep-detox',
    name: 'Deep Detox',
    tagline: 'Glow Up + Breathing Room Services',
    icon: '💧',
    accent: 'from-sky-200 to-cyan-100',
    accentText: 'text-sky-700',
    features: [
      'Everything in The Glow Up',
      '4 Ionic Foot Detoxes',
      '2 Biofield (AO) Scans',
      '2 PEMF / Red Light treatments',
      'Frequency treatments',
      'Class access',
    ],
    summary: [
      'A genuine, full-body reset with the support of Breathing Room\'s frequency tools. Deep Detox gives you everything in The Glow Up — and layers four Ionic Foot Detoxes, two Biofield (AO) Scans, two PEMF / Red Light treatments, frequency treatments, and class access on top.',
      'These services give the program teeth. The Biofield scans help us see what your body is asking for; the foot baths and frequency work help it release at a depth food and fascia alone can\'t always reach.',
      'Best for: anyone navigating chronic patterns, persistent symptoms, or anyone who wants the program to do real work in real ways.',
    ],
    price: '$449',
    valueNote: 'Over $800 in value when bundled into the program',
    priceBlurb: 'Everything in The Glow Up — plus hands-on Breathing Room frequency services that do the deeper work. Serious support, beautifully bundled.',
  },
  {
    id: 3,
    slug: 'full-immersion',
    name: 'Full Immersion',
    tagline: 'The Full Monty',
    icon: '✨',
    accent: 'from-violet-200 to-purple-100',
    accentText: 'text-violet-700',
    features: [
      'Everything in The Glow Up',
      '4 Ionic Foot Detoxes',
      '4 PEMF / Red Light treatments',
      '3 AO Biofield Scans',
      'Frequency treatments',
      '2 private consult sessions with Jill for personalized wellness projections',
      '4 supportive services of choice — AI Massage Chair, Float Therapy, Sound Therapy Lounger',
      'Access to all in-person classes',
    ],
    summary: [
      'The full monty. Everything in The Glow Up, layered with the deepest support Breathing Room offers — 4 Ionic Foot Detoxes, 4 PEMF / Red Light treatments, 3 AO Biofield Scans, frequency treatments, and full access to in-person classes throughout your 21 days.',
      'You\'ll also have two private consult sessions with Jill where we sit together and map specific wellness projections for your body, your life, your direction. On top of that — 4 supportive services of your choice from the AI Massage Chair, Float Therapy, and Sound Therapy Lounger — sensory tools that help your nervous system land deeper into rest and integration.',
      'Best for: anyone ready to invest in real, embodied change with concierge-level support and a felt sense of total nourishment.',
    ],
  },
];

// ============ THE 7 SYSTEMS ============
const systems = [
  {
    id: 'root',
    name: 'The Root',
    color: 'red',
    hex: '#c43d3d',
    bg: 'from-red-50 to-rose-50',
    border: 'border-red-200',
    pill: 'bg-red-100 text-red-700 border-red-200',
    swatch: 'bg-red-500',
    days: [1, 2, 3],
    endocrineGland: 'Adrenals',
    enhancementsIntro: "Welcome to the first part of the detox — The Root. The Root is about groundedness. It's about healing. It's about intention. So for these three days, consider what your intention is. The first three days are novel and interesting — they may feel a little overwhelming, so we start pretty simple. Make some sort of juice or elixir that appeals to you. Find a tea or hydration that works for you. Even make something like a big pan of roasted root vegetables to get you through, and make proteins to your desire as we start to wiggle in and get our feet wet. So focus on setting intentions, setting the stage, and taking it nice and slow. Try the fascial maneuvers, slowly dipping your toes in — it's not a hard practice, but it is a new practice.",
    anatomy: ['Blood cells', 'Bones', 'DNA', 'Immune system', 'Joints', 'Legs and feet', 'Muscles', 'Rectum', 'Skin', 'Tailbone (coccyx)'],
    physiology: ['Enzyme activity', 'Flight-or-fight response', 'Gene expression', 'Protein production'],
    coreIssues: ['Safety', 'Survival', 'Tribe'],
    foods: ['Dietary proteins', 'Immune-enhancing foods', 'Insoluble fiber', 'Mineral-rich foods', 'Root vegetables', 'Red-colored foods'],
    foodGroups: [
      {
        label: 'Red Foods',
        note: 'These don\'t magically heal The Root — but traditionally they\'re associated with grounding, blood-building, vitality, and survival energy.',
        items: ['Beets', 'Cherries', 'Strawberries', 'Raspberries', 'Red cabbage', 'Red onion', 'Red bell peppers', 'Cranberries', 'Pomegranate', 'Red grapes', 'Tomatoes', 'Watermelon', 'Hibiscus tea', 'Radishes'],
      },
      {
        label: 'Root Vegetables',
        items: ['Sweet potato', 'Carrot', 'Beet', 'Parsnip', 'Turnip', 'Rutabaga', 'Radish', 'Celery root', 'Daikon', 'Yams'],
      },
      {
        label: 'Mineral-Rich Foods',
        items: ['Sea vegetables', 'Pumpkin seeds', 'Sesame seeds', 'Sunflower seeds', 'Sardines', 'Wild salmon', 'Grass-fed beef', 'Lentils', 'Chickpeas', 'Swiss chard', 'Kale', 'Spinach', 'Sea salt', 'Bone broth'],
      },
      {
        label: 'Blood-Tonifying Foods',
        note: 'Chinese Medicine inspired.',
        items: ['Beets', 'Blackberries', 'Cherries', 'Red dates (jujube)', 'Dark leafy greens', 'Grass-fed beef', 'Liver (for the adventurous)', 'Egg yolks', 'Lentils', 'Black beans', 'Molasses', 'Pomegranate'],
      },
    ],
    teas: ['Nettle', 'Hibiscus', 'Ginger', 'Dandelion root', 'Red raspberry leaf'],
    herbs: ['Ashwagandha', 'Rhodiola', 'Eleuthero', 'Astragalus', 'Reishi'],
    minerals: ['Magnesium', 'Trace minerals', 'Sea salt in water', 'Silica-rich mineral water'],
    affirmation: 'I am safe. I am grounded. I belong.',
    prep: {
      title: 'Prepping for The Root',
      intro: 'Before Day 1, take a beat to gather what you need and tune your mind. This is the foundation phase — start where you actually are, not where you wish you were.',
      shopping: [
        'A small selection of red foods that excite you (beets, cherries, pomegranate, tomatoes…)',
        '2–3 root vegetables (sweet potato, carrot, beet are my favourites)',
        'A mineral source you\'ll actually use — sea salt, bone broth, or a small bag of seeds',
        'One Root tea to sip slowly each day (Nettle is a beautiful start)',
        'High-quality water — filtered or mineral',
      ],
      mindset: [
        'Spend 5 minutes the night before noticing where you feel rooted (or not) in your body',
        'Set one intention for these 3 days: not a goal — an intention',
        'Clear the clutter from your kitchen counter so nourishment feels like a small ritual, not a chore',
      ],
      gentle: 'These 3 days are not a test. They\'re a tuning. Eat slowly. Stand on the earth if you can. Trust that the body wants this.',
    },
  },
  {
    id: 'flow',
    name: 'The Flow',
    color: 'orange',
    hex: '#e07b3a',
    bg: 'from-orange-50 to-amber-50',
    border: 'border-orange-200',
    pill: 'bg-orange-100 text-orange-700 border-orange-200',
    swatch: 'bg-orange-500',
    days: [4, 5, 6],
    endocrineGland: 'Ovaries / Testes',
    enhancementsIntro: "Consider making one or both of the Lemon Elixir or the Pineapple Ginger Elixir this week. Lemon is more for clarifying, and the pineapple is rich in enzymes for digestion. I often have 1/2–1 cup in the AM, and I'll absolutely drink one if I've become over-hungry or want to take the edge off my appetite. Enjoy any foods from the list and think orange-ish. Toward the end of this week, around the 7–8 day mark of your journey, consider the Liver Flush — we'll discuss it at the first meeting. Be sure to do at least one fascia practice a day; the Stress Reset and Organ Reset will be the biggest value (the 1st and 2nd day of each section). I place a varied practice on the 3rd day of each section to support whichever organs and energy meridians we're working on. The Root started with heavier, earthy foods, and now we'll begin to lighten up a little. :)",
    anatomy: ['Bladder', 'Hips', 'Kidneys', 'Large intestine', 'Reproductive system', 'Sacrum'],
    physiology: ['Cellular replication', 'Fat storage', 'Reproduction', 'Water balance'],
    coreIssues: ['Creativity', 'Emotions', 'Relationships'],
    foods: ['Dietary fats and oils', 'Fermented foods', 'Fish and seafood', 'Nuts and seeds', 'Tropical foods', 'Water', 'Orange-colored foods'],
    foodsNarrative: 'Balance your sacral chakra with all things orange. Think carrots, mango, oranges, orange peppers, peaches, apricots, and sweet potatoes. Foods rich in Omega-3s, such as salmon, also work well to balance this chakra. Nuts and seeds such as flax, almonds, walnuts, and sesame provide additional fatty Omega-3s, which aid in cardiovascular health and reduce inflammation. Also stock up on coconut and spices such as cinnamon. Since this chakra\u2019s element is water, stay hydrated and drink plenty of water, coconut water, or herbal teas.',
    affirmation: 'I flow with life. I am creative. I welcome pleasure.',
  },
  {
    id: 'fire',
    name: 'The Fire',
    color: 'yellow',
    hex: '#d6b227',
    bg: 'from-yellow-50 to-amber-50',
    border: 'border-yellow-200',
    pill: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    swatch: 'bg-yellow-400',
    days: [7, 8, 9],
    endocrineGland: 'Pancreas',
    anatomy: ['Gallbladder', 'Liver', 'Small intestine', 'Stomach'],
    physiology: ['Assimilation', 'Biotransformation', 'Blood sugar balance', 'Digestion'],
    coreIssues: ['Balance', 'Energy', 'Power'],
    foods: ['Dietary carbohydrates', 'Healthy sweeteners', 'Legumes', 'Soluble fiber', 'Whole grains', 'Yellow-colored foods'],
    affirmation: 'I am powerful. I trust my inner fire.',
  },
  {
    id: 'love',
    name: 'The Love',
    color: 'green',
    hex: '#5b9d63',
    bg: 'from-green-50 to-emerald-50',
    border: 'border-green-200',
    pill: 'bg-green-100 text-green-700 border-green-200',
    swatch: 'bg-green-500',
    days: [10, 11, 12],
    endocrineGland: 'Thymus & Heart',
    anatomy: ['Armpits', 'Arms', 'Blood vessels', 'Breasts', 'Hands', 'Lungs', 'Lymphatic system', 'Shoulders', 'Wrists'],
    physiology: ['Breathing', 'Circulation', 'Oxygenation'],
    coreIssues: ['Compassion', 'Expansion', 'Service'],
    foods: ['Leafy vegetables', 'Microgreens', 'Phytonutrients', 'Sprouts', 'Vegetables (especially green)'],
    enhancementsIntro: "As we move into the green chakra — the love chakra — foods get a lot lighter, a little waterier, a little more in the flow. You're more in the flow, so foods will feel very different. Foods here are more activating of the body's detox process. Having a celery juice in the AM will support a lot of toxins moving. The Love section is also a beautiful time to consider self-compassion, self-love, and gently meeting your limiting beliefs — with self-compassion.",
    affirmation: 'I am open to love. I give and receive freely.',
  },
  {
    id: 'truth',
    name: 'The Truth',
    color: 'blue',
    hex: '#3a76b8',
    bg: 'from-sky-50 to-blue-50',
    border: 'border-sky-200',
    pill: 'bg-sky-100 text-sky-700 border-sky-200',
    swatch: 'bg-sky-500',
    days: [13, 14, 15],
    endocrineGland: 'Thyroid',
    anatomy: ['Cheeks', 'Chin', 'Ears', 'Mouth', 'Neck', 'Nose', 'Throat'],
    physiology: ['Chewing', 'Hearing', 'Metabolism', 'Smelling', 'Speaking'],
    coreIssues: ['Authenticity', 'Choice', 'Voice'],
    foods: ['Fruits', 'Juices', 'Sauces', 'Sea plants', 'Soups', 'Teas'],
    teas: ['Peach', 'Skullcap', 'Bayberry', 'Honeybush', 'Red clover', 'Homemade Electrolyte Drink', 'Iced Citrus Electrolyte Drink'],
    affirmation: 'I speak my truth. My voice matters.',
  },
  {
    id: 'insight',
    name: 'The Insight',
    color: 'indigo',
    hex: '#5a4ba3',
    bg: 'from-indigo-50 to-violet-50',
    border: 'border-indigo-200',
    pill: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    swatch: 'bg-indigo-500',
    days: [16, 17, 18],
    endocrineGland: 'Pituitary',
    anatomy: ['Brain', 'Eyebrows', 'Eyes', 'Forehead', 'Neurons', 'Neurotransmitters'],
    physiology: ['Mood balance', 'Sleep', 'Thought processing'],
    coreIssues: ['Intuition', 'Reflection', 'Visualization'],
    foods: ['Caffeine', 'Chocolate / cocoa', 'Mood-modulating foods', 'Spices', 'Blue-purple foods'],
    affirmation: 'I trust my inner wisdom. I see clearly.',
  },
  {
    id: 'spirit',
    name: 'The Spirit',
    color: 'violet',
    hex: '#8a6fbf',
    bg: 'from-violet-50 to-purple-50',
    border: 'border-violet-200',
    pill: 'bg-violet-100 text-violet-700 border-violet-200',
    swatch: 'bg-violet-400',
    days: [19, 20, 21],
    endocrineGland: 'Pineal',
    anatomy: ['Electromagnetic field', 'Energy meridians', 'Nervous system'],
    physiology: ['Circadian rhythms', 'Cleansing', 'Light sensitivity'],
    coreIssues: ['Connection', 'Purpose', 'Soul'],
    foods: ['Fasting & detoxification practices', 'Photons', 'Toxin-free foods'],
    enhancementsIntro: "Welcome to The Spirit — the crown. Here we're guided by Wisdom, Insight, and Connection. As you move through these final days, let three simple practices carry you. First, remember that intention matters — energy follows intent — so set yours gently before each meal and each practice. Second, create a harmonious meal environment; a calm space creates calm digestion, so slow down, soften, and let your meals be unhurried. And third, offer a small blessing or gratitude practice — thank or bless your food, your body, and your higher source, whatever that looks like for you. Foods here naturally lighten and rise: think vibrant violets and purples, pure whites, and light, hydrating choices (explore the crown foods below). Let this be a spacious, reflective close to your journey.",
    foodGuide: {
      title: 'Top Crown Chakra Foods to Incorporate',
      intro: 'Nourishing your seventh chakra can be a delightful culinary exploration. Focusing on specific colors and types of foods can help attune your body to the subtle energies that support the crown chakra.',
      groups: [
        {
          name: 'Vibrant Violet & Purple',
          blurb: 'Deeply hued, antioxidant-rich foods thought to stimulate spiritual awareness and intuition.',
          items: [
            { name: 'Blueberries & Blackberries', note: 'Packed with antioxidants — brain-supportive, high-vibration foods.' },
            { name: 'Eggplant (with skin)', note: 'Its deep violet resonates with the crown; opt for lighter cooking methods.' },
            { name: 'Purple Grapes & Plums', note: 'Sweet and hydrating, offering a gentle energy.' },
            { name: 'Culinary Lavender', note: 'Infused into teas or light desserts — calming and spiritually uplifting.' },
          ],
        },
        {
          name: 'Pure White & Translucent',
          blurb: 'Foods that symbolize purity, clarity, and the influx of divine light.',
          items: [
            { name: 'Cauliflower', note: 'Versatile and light — prepare it in many healthy ways.' },
            { name: 'Mushrooms', note: 'Lighter varieties connect us to the earth\u2019s energy and spiritual insight.' },
            { name: 'Garlic & Onions', note: 'Purifying — they help cleanse energetic blockages.' },
            { name: 'Coconut', note: 'Both the water and flesh are hydrating and clarifying.' },
            { name: 'Lychee & White Asparagus', note: 'Delicate flavors and light energy.' },
          ],
        },
        {
          name: 'Light & Hydrating',
          blurb: 'High-water-content foods that keep your energy flowing.',
          items: [
            { name: 'Melons (Watermelon, Cantaloupe)', note: 'High water content helps cleanse and hydrate the system.' },
            { name: 'Clear Herbal Teas', note: 'Chamomile, peppermint, or ginger — soothing and clarifying.' },
            { name: 'Clear Vegetable Broths', note: 'Light, nourishing, and easy to digest.' },
          ],
        },
      ],
    },
    affirmation: 'I am connected to all that is. I am light.',
  },
];

const bonusWeek = {
  id: 'full-spectrum',
  name: 'Living in the Full Spectrum',
  tagline: 'Optional Week 4 — double down right when you think you\'re done',
  days: [22, 23, 24, 25, 26, 27, 28],
  description: 'A bonus week for those ready to keep going. Choose your colors based on what calls you, your AO scan, or repeat favorites from the journey. Double your fascia practice each day (virtual, video, or in-person).',
};

// ============ DAILY FASCIA PRACTICES (keyed by day number) ============
const dayPractices = {
  1: {
    title: 'First Fascia Practice — The Stress Reset',
    embedId: 'IB10DIqV_b0',
    instruction: "Welcome to your very first fascia practice! This gentle Stress Reset is the perfect way to begin The Root. Find a quiet space, give yourself permission to slow down, and simply follow along. There's no right or wrong — let your body move at its own pace and notice how you feel afterward. Aim to do this once today; repeat anytime you feel tension building.",
  },
  2: {
    title: 'Gentle Lying Down Organ Reset',
    embedId: 'hv--9WSB0l4',
    instruction: "Today's practice is a gentle, lying-down organ reset — a beautiful way to keep grounding into The Root. Find a comfortable spot on the floor or your bed, get cozy, and let your body fully release into the surface beneath you. Follow along slowly and breathe. There's nothing to force here; simply allow the gentle movement to support your organs and nervous system. Notice how you feel when you're done.",
  },
  3: {
    title: 'Lower Body Reset — Wake Up & Free the Legs',
    embedId: 'hs9RROigS0Q',
    instruction: "Today we bring the practice down into the legs — waking them up and freeing the lower body. Find a little space, follow along at your own pace, and notice how your legs and hips feel afterward. Gentle and unhurried is the goal; let the movement invite ease into the whole lower half of your body.",
  },
  4: {
    title: 'Chair Stress Reset + Swinger & Elbow Torque',
    embedId: 'qfDJOckl2jk',
    instruction: "Welcome to The Flow! Today's practice is a Chair Stress Reset paired with the Swinger and Elbow Torque — an easy, accessible way to keep things moving. You can do this right from a chair, so it's perfect for a work break or any time you need to shake off tension. Follow along at your own pace and let the gentle swinging and torquing free up your upper body and get your energy flowing.",
  },
  5: {
    title: 'Organ Reset',
    embedId: 'EcEkqCtqSx8',
    instruction: "This one can be done daily or whenever you feel inspired. Mechanically stimulating our organ system — and giving it more energy — can become one of the most effective practices we ever adopt. This gentle reset encourages circulation, lymphatic movement, and fresh energy to the digestive and detox organs, helping your whole system flow more freely. Find a comfortable position, follow along, breathe deeply, and let your body settle into the reset.",
    affirmations: [
      'My organs are supported, nourished, and working in beautiful harmony.',
      'With every breath, I bring fresh energy and circulation to my body.',
      'I trust my body\'s natural ability to cleanse, renew, and restore.',
      'Energy flows freely through me — I move with ease and lightness.',
    ],
  },
  6: {
    title: 'Fascial Coffee — Waking Up Your Fascia',
    embedId: 'jAS8KmPd0Ls',
    instruction: "Fascial Coffee — waking up your fascia! This is an exploratory session to feel, experience, and get curious about your fascia and the subtle sensations it creates in the body, while waking up our energy and electrical systems. There's no right or wrong here; simply follow along, stay curious, and notice what you feel.",
  },
  7: {
    title: 'Stress Reset + Swinger, Psoas Release & QiGong Arm Swings',
    embedId: '8tPrX2GgL_A',
    instruction: "Welcome to The Fire! Today we build a little heat and momentum with a Stress Reset, the Swinger, a Psoas Release, and QiGong arm swings. The psoas is deeply tied to how we hold stress, so give it some love — and let the arm swings get your energy circulating. Follow along at your own pace and enjoy the flow.",
  },
  8: {
    title: 'Restorative Organ Reset — Light & Light Quality',
    embedId: 'yimMFzsv2_I',
    instruction: "A restorative Organ Reset to keep The Fire flowing — and a little about light and light quality. The light we take in shapes our rhythms and energy deeply. Aim for natural sunlight early in the day, get outside when you can, and soften artificial and blue light in the evenings so your body can wind down. Consider warm, low light or candlelight at night. Follow along with the reset, breathe, and let both movement and light restore you.",
  },
  9: {
    title: 'Stress Reset — Palate, Totally Twisted, Psoas Release & Jaw Release',
    embedId: 'jzOa6eOQKDM',
    instruction: "Today's Stress Reset moves through the palate, Totally Twisted, a psoas release, and a jaw release. We hold so much tension in the jaw and palate, so give those areas some gentle attention — and let the twists and psoas work release the deeper holding patterns. Follow along at your own pace and breathe.",
  },
  10: {
    title: 'Totally Twisted, Swinger, Pull Over & Sinus Reset',
    embedId: 'XMdErCVOOUs',
    instruction: "Welcome to The Love. Today we flow through Totally Twisted, the Swinger, a Pull Over, and a Sinus Reset. The sinus work can feel especially clearing and light. Follow along at your own pace, breathe, and enjoy opening through the chest and head.",
  },
  11: {
    title: 'Totally Twisted, Pretzel Squat & Chest Release',
    embedId: 'wAXBKPzH9X4',
    instruction: "Today we move through Totally Twisted, a Pretzel Squat, and a chest release. The pretzel squat opens the hips while the chest release invites your heart space to soften and expand. Follow along at your own pace and breathe into the opening.",
  },
  12: {
    title: 'Fascial Coffee & Upper Body Reset — Heart Flow Opening Practice',
    embedId: 'b38UD_SyY3U',
    instruction: "We close out The Love with a heart flow opening. Let your chest soften and your breath expand as you move through this Fascial Coffee & Upper Body Reset — an invitation to release, open, and receive. Follow along at your own pace and notice how your heart space feels afterward.",
  },
  13: {
    title: 'Facial Fascial Massage, Totally Twisted, Anti-Gravity & Ear Pull/Twist',
    embedId: 'pEOet1cf_z8',
    instruction: "Welcome to The Truth. Today's practice is a lovely mix: a Facial Fascial Massage, Totally Twisted, Anti-Gravity, and an Ear Pull/Twist. Give your face, ears, and whole body some gentle attention and let the twists release anything that's ready to go. Follow along at your own pace and enjoy.",
  },
  14: {
    title: 'Organ Reset — Light & Light Sources',
    embedId: 'T4_BPLooxpI',
    instruction: "Another Organ Reset to keep your system flowing — and this week, a gentle invitation to notice your light. The light we take in shapes our rhythms deeply: aim for natural sunlight early in the day to set your body clock, get outside when you can, and soften the artificial and blue light in the evenings (screens, overhead lights) so your body can wind down. Consider warm, low lighting or candlelight at night, and let the morning sun be your first light source when possible. Follow along with the reset, breathe, and let both movement and light support your truth and rhythm.",
  },
  15: {
    title: 'Fascial Coffee & Upper Body Reset — Twisty Wristy, Elbow Torque, Psoas Release & Vagus Nerve Reset',
    embedId: 'b38UD_SyY3U',
    instruction: "We round out The Truth with a Fascial Coffee & Upper Body Reset — twisty wristy, elbow torque, psoas release, and a vagus nerve reset. The vagus nerve is your body's calming superhighway, so this one helps settle the whole system. Follow along at your own pace and let your upper body, wrists, and nervous system unwind.",
  },
  16: {
    title: 'Stress Reset — Vagus Nerve, Totally Twisted, Swinger & Twisty Wristy',
    embedId: 'oE3vST404f0',
    instruction: "Welcome to The Insight. Today's Stress Reset moves through a vagus nerve reset, Totally Twisted, the Swinger, and Twisty Wristy. Let the vagus nerve work calm your whole system while the twists and swings free up your upper body. Follow along at your own pace, breathe, and settle into this deeper, more intuitive stretch of the journey.",
  },
  17: {
    title: 'Extremities Practice — Ankles, Achilles, Knees, Elbow Torque, Collarbone, Nasal Bone & Ear Pull',
    embedId: '0uL4BTVS3gk',
    instruction: "Today we work the extremities — ankles, Achilles, the back and front of the knees, elbow torque, collarbone, nasal bone, and an ear pull. These smaller, often-forgotten areas hold a surprising amount of tension and fascial restriction. Give each one gentle, curious attention and notice how freeing up the edges of your body creates more ease everywhere. Follow along at your own pace and breathe.",
  },
  18: {
    title: 'Organ Reset with Affirmations',
    embedId: '4HlaO_q08Ao',
    instruction: "Today's Organ Reset pairs gentle movement with affirmations — a beautiful way to nourish The Insight. As you move, let the words settle in and support your organs, your energy, and your inner knowing. Find a comfortable position, follow along, breathe deeply, and let both the movement and the affirmations restore you.",
    affirmations: [
      'My body is wise, and I trust its quiet guidance.',
      'With every breath, I nourish and renew my organs.',
      'I am clear, calm, and connected to my inner knowing.',
      'Energy flows freely through me — I am restored.',
    ],
  },
  19: {
    title: 'Facial Fascial Massage, Totally Twisted, Anti-Gravity & Ear Pull/Twist',
    embedId: 'pEOet1cf_z8',
    instruction: "Welcome to The Spirit — we're in the home stretch. Today's practice is a lovely mix: a Facial Fascial Massage, Totally Twisted, Anti-Gravity, and an Ear Pull/Twist. Give your face, ears, and whole body some gentle attention and let the twists release anything that's ready to go. Follow along at your own pace and enjoy releasing into these final days.",
  },
  20: {
    title: 'Totally Twisted, Anti-Gravity, Cheek Release & Sinus Release',
    embedId: '1g9XUe05lUg',
    instruction: "Today we flow through Totally Twisted, Anti-Gravity, a Cheek Release, and a Sinus Release. The face, cheeks, and sinuses hold so much subtle tension — releasing here can feel wonderfully clearing and light. Follow along at your own pace, breathe, and let this practice open and lift you as you move deeper into The Spirit.",
  },
  21: {
    title: 'Stress Reset — Totally Twisted, Anti-Gravity, Elbow Torque & Forehead Release',
    embedId: 'c-8RuEgU3Fw',
    instruction: "Your final day — beautifully done. Today's closing Stress Reset moves through Totally Twisted, Anti-Gravity, Elbow Torque, and a Forehead Release. Let this be a moment to honor how far you've come. Move slowly, breathe deeply, and soften the forehead and mind as you complete your journey through The Spirit. Carry this ease and awareness with you.",
  },
};

// ============ FOODS FOR THE DETOX — OVERVIEW TABLE ============
const detoxFoodOverview = detoxFoodOverviewData;

// ============ MEAT SHOPPING — DECODING BUZZ WORDS ============
const meatGuide = {
  title: 'Meat Shopping — Decoding Buzz Words',
  intro: "There's lots of doom and gloom documentaries about the horrible-ness of our food system. The dilemma is how to feed the world and practices are getting weird. I like to be informed, talk to local farmers, read labels.",
  helpful: {
    title: 'Helpful Buzz Words',
    items: ['100% grass fed', 'Grass finished', 'Free-range', 'Free-roaming', 'Pasture raised', 'Ethically raised'],
  },
  misleading: {
    title: 'Misleading Buzz Words',
    note: "Still helpful, but not all it's cracked up to sound like",
    items: [
      { term: 'Grass fed', explanation: '80% of life is grass fed, but other 20% is unknown' },
      { term: 'Antibiotic/hormone free', explanation: 'Great aspect, but no idea what else is happening' },
      { term: 'Organic', explanation: 'Likely means animals fed organic grains — still not their natural diet' },
    ],
  },
  unhelpful: {
    title: 'Unhelpful Buzz Words — Avoid!',
    items: [
      { term: 'Vegetarian fed', explanation: "Weird! Cement and cardboard are vegetarian too — it's trickery!" },
      { term: 'No buzz words', explanation: 'Means factory practices, usually unethical, antibiotics and hormones' },
    ],
  },
  bermuda: {
    title: 'Bermuda Meat Shopping',
    intro: "Many years ago I did a pretty deep dive into ethical and healthy meats. It's a lot of work, so I haven't strayed too much from these brands :) I've honed my intuition to where I can usually tell if something's decent — and we can practice this together.",
    brands: [
      { name: 'Bell & Evans (green banner) chicken', note: 'They come in blue and green — green is the higher quality.' },
      { name: 'Niman Ranch', note: 'Beef and other sausage products.' },
    ],
    extra: "We have some bits from the UK / Waitrose now too. I'll shop at Miles — though most of Miles' stuff is unlabeled, so it's hard to know where it's from.",
    closing: "I'm also so sensitive that once I eat something, I'll know whether it's decent or not — and if it's not, I won't buy it again.",
  },
};

// A typical day's rhythm during the detox
const detoxDailyRhythm = [
  { part: 'Morning', items: ['Begin with warm lemon water and a few quiet breaths', 'Breakfast', 'Set an intention for the day'] },
  { part: 'Afternoon', items: ['Lunch', 'Snack', 'Hydrate well between meals'] },
  { part: 'Evening', items: ['Before you eat dinner, complete the movement portion of your day', 'Dinner', 'As the day winds down, take time to meditate', 'As you prepare for bed, note the day\u2019s emotions in your Emotion Log'] },
];


// ============ HELPERS ============
// Tier benefits (Glow Up = 1, Deep Detox = 2, Full Immersion = 3)
const getTierBenefits = (tierId) => ({
  hasFood: tierId >= 1,       // All tiers
  hasFascia: tierId >= 1,     // Glow Up + (fascia now included from tier 1)
  hasDetox: tierId >= 2,      // Deep Detox +
  hasBR: tierId >= 2,         // Deep Detox + (Breathing Room services)
  hasPrivate: tierId >= 3,    // Full Immersion (unlimited classes + private sessions)
});

// ============ PAGE ============
const FlexDetoxPage = () => {
  const { user, hasAccess } = useAuth();
  const { trackView, trackSectionClick } = useActivityTracker('flex-detox');
  const hasFullAccess = hasAccess('flex-detox');

  const tierStorageKey = user?.id ? `flex-detox-tier-${user.id}` : null;
  const bonusStorageKey = user?.id ? `flex-detox-bonus-${user.id}` : null;
  const completedStorageKey = user?.id ? `flex-detox-completed-${user.id}` : null;
  const favoriteStorageKey = user?.id ? `flex-detox-favorites-${user.id}` : null;

  const [selectedTier, setSelectedTier] = useState(() => {
    try {
      const key = user?.id ? `flex-detox-tier-${user.id}` : null;
      const v = key ? localStorage.getItem(key) : null;
      return v ? Number(v) : null;
    } catch (e) { return null; }
  });
  const [bonusWeekOn, setBonusWeekOn] = useState(() => {
    try {
      const key = user?.id ? `flex-detox-bonus-${user.id}` : null;
      return key ? localStorage.getItem(key) === '1' : false;
    } catch (e) { return false; }
  });
  const [completedDays, setCompletedDays] = useState(() => {
    try {
      const key = user?.id ? `flex-detox-completed-${user.id}` : null;
      const v = key ? localStorage.getItem(key) : null;
      return v ? JSON.parse(v) : {};
    } catch (e) { return {}; }
  });
  const [favoriteDays, setFavoriteDays] = useState(() => {
    try {
      const key = user?.id ? `flex-detox-favorites-${user.id}` : null;
      const v = key ? localStorage.getItem(key) : null;
      return v ? JSON.parse(v) : {};
    } catch (e) { return {}; }
  });
  const [expandedSystem, setExpandedSystem] = useState('root');
  const [introOpen, setIntroOpen] = useState(false);
  const [depthOpen, setDepthOpen] = useState(false);
  const [prepOpen, setPrepOpen] = useState(false);
  const [mealMapOpen, setMealMapOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [infoTier, setInfoTier] = useState(null);
  const [showMeatGuide, setShowMeatGuide] = useState(false);
  const [inquiry, setInquiry] = useState(null); // { tierName?, source }
  const openInquiry = (opts) => setInquiry({ ...opts, openedAt: Date.now() });

  const saveTier = (tierId) => {
    setSelectedTier(tierId);
    if (tierStorageKey) localStorage.setItem(tierStorageKey, String(tierId));
    trackSectionClick(`tier-${tierId}`);
  };

  const toggleBonus = () => {
    const next = !bonusWeekOn;
    setBonusWeekOn(next);
    if (bonusStorageKey) localStorage.setItem(bonusStorageKey, next ? '1' : '0');
  };

  const toggleDayComplete = (dayNum) => {
    const next = { ...completedDays, [dayNum]: !completedDays[dayNum] };
    setCompletedDays(next);
    if (completedStorageKey) localStorage.setItem(completedStorageKey, JSON.stringify(next));
  };

  const toggleFavorite = (dayNum) => {
    const next = { ...favoriteDays, [dayNum]: !favoriteDays[dayNum] };
    setFavoriteDays(next);
    if (favoriteStorageKey) localStorage.setItem(favoriteStorageKey, JSON.stringify(next));
  };

  const totalDays = bonusWeekOn ? 28 : 21;
  const completedCount = Object.values(completedDays).filter(Boolean).length;
  const progressPct = Math.round((completedCount / totalDays) * 100);

  const benefits = useMemo(() => getTierBenefits(selectedTier || 0), [selectedTier]);

  const openDay = (system, dayNum) => {
    setSelectedDay({ system, dayNum });
    trackView(`day-${dayNum}`, `${system.name} · Day ${dayNum}`);
  };

  // ============ LOCKED VIEW (Public Preview) ============
  if (!hasFullAccess) {
    return (
      <div className="min-h-screen bg-cream pb-20">
        {/* HERO */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-red-200/40 via-yellow-200/40 via-green-200/40 via-blue-200/40 to-violet-300/40 pointer-events-none" />
          <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-12">
            <Link
              to="/programs"
              className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-6"
              data-testid="flex-detox-back-link"
            >
              <ArrowLeft size={18} /> Back to Programs
            </Link>
            <p className="text-xs font-body text-forest/70 uppercase tracking-[0.25em] mb-3">A 21-Day Holistic Detox</p>
            <h1 className="font-heading text-4xl md:text-6xl text-forest mb-4">The Flex Detox</h1>
            <p className="text-stone-700 font-body text-lg max-w-2xl leading-relaxed">
              Eat with the colors. Move with your body. Choose your depth.
              <br />
              A seven-system nourishment journey through The Root, The Flow, The Fire, The Love, The Truth, The Insight, and The Spirit — designed to make detoxing feel less like restriction and more like aligning to our own wellness and bodily needs, moving towards thriving.
            </p>

            {/* System rainbow */}
            <div className="flex items-center gap-1 mt-8">
              {systems.map((c) => (
                <div key={c.id} className="flex-1 group">
                  <div className={`h-3 ${c.swatch} rounded-full transition-transform group-hover:scale-y-150 origin-bottom`} />
                  <p className="text-[10px] font-body text-forest/60 mt-1 text-center">{c.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* OVERVIEW NARRATIVE */}
        <section className="max-w-3xl mx-auto px-6 mt-14">
          <div className="space-y-5 font-body text-stone-700 leading-relaxed">
            <p className="font-heading text-xl md:text-2xl text-forest italic">
              The Flex Detox is your plan, your pace. We bring the guidance, support, and potentially supportive compliments.
            </p>
            <p>A unique way to recover health and enhance wellness.</p>
            <p>Whether you&apos;re working through a wellness mystery or wanting to augment your current wellness into vitality — we&apos;ve got you.</p>
            <p className="font-heading text-lg text-forest">Choose your own path. Choose your own depth.</p>
            <p className="text-sm text-stone-500">Prices vary based on the services and depth you choose. We will support and guide you the best we can so you can support yourself the best you can.</p>

            <div className="bg-white/70 rounded-2xl p-6 border border-stone-200">
              <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-3">What unfolds</p>
              <p className="mb-4">
                A fully guided program — foods, food choices, shopping lists. The unfolding is themed to cascade through what our bodies need to create shift:
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  'Tone the blood',
                  'Prep the system',
                  'Balance gut and hormones',
                  'Invite The Love Philosophy for healing from the inside out',
                  'Step into self-empowerment using food as medicine',
                  'Healing movement to support the other great work',
                ].map((s) => (
                  <li key={s} className="flex items-start gap-2 text-sm">
                    <span className="text-terracotta mt-1">✦</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm italic text-stone-600">
                There&apos;s plenty of food — you will NOT feel deprived. And joy is a huge part of this process.
              </p>
            </div>

            <p>
              We are dabbling in a coordinated way. Your body will begin to show you the philosophies that are most supportive for your system — so you can build small, sustainable practices that feel <em>productive and beneficial</em>.
            </p>
            <p className="font-heading text-base text-forest italic">
              One of the worst feelings is to spend time, energy, and effort on our wellness only to feel just as lost, ineffective, or boomerang. This is a different way.
            </p>
            <p>
              We&apos;ve also built more immersive options with Biofield scans, frequency treatments, ionic foot baths, and a few of our other services to really elevate your wellness journey. There will be fascial maneuvers involved.
            </p>
          </div>
        </section>

        {/* ASSESSMENT DIRECTIONS */}
        <section className="max-w-6xl mx-auto px-6 mt-16">
          <div className="rounded-2xl bg-gradient-to-br from-forest to-forest-dark text-cream p-8 md:p-10" data-testid="flex-detox-assessment-banner">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 mb-3">
                  <Sparkles size={18} className="text-terracotta" />
                  <span className="font-body text-sm tracking-widest uppercase text-cream/70">Not sure where to start?</span>
                </div>
                <h2 className="font-heading text-2xl md:text-3xl mb-3">Take the &ldquo;What Is Your Body Asking For?&rdquo; Assessment</h2>
                <p className="font-body text-cream/85 leading-relaxed">
                  Before you choose your depth, take this free 4-minute assessment. Thirty short statements across six body
                  systems reveal where your body is carrying the most load — so you can walk into the Flex Detox knowing
                  your best direction. Open to everyone, and especially helpful for participants.
                </p>
              </div>
              <div className="flex-shrink-0">
                <Link
                  to="/quiz/body-assessment"
                  className="inline-flex items-center gap-2 bg-terracotta text-white px-7 py-4 rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors"
                  data-testid="flex-detox-assessment-cta"
                >
                  Take the Assessment <ChevronRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FOOD PREFERENCES CTA (private clients) */}
        <section className="max-w-6xl mx-auto px-6 mt-8">
          <div className="rounded-2xl bg-[#f5efe4] border border-[#e8dcc3] p-8 md:p-10" data-testid="flex-detox-foodprefs-banner">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 mb-3">
                  <UtensilsCrossed size={18} className="text-terracotta" />
                  <span className="font-body text-sm tracking-widest uppercase text-[#8a6a5a]">Private Clients · Food Guide</span>
                </div>
                <h2 className="font-heading text-2xl md:text-3xl text-forest mb-3">Share Your Food Preferences</h2>
                <p className="font-body text-stone-600 leading-relaxed">
                  Working with Jill one-on-one? Tick the foods you love and the ones you'd rather avoid — across
                  vegetables, proteins, nuts &amp; fats, and grains — plus a note for anything not listed. Jill reads
                  your picks and shapes a recipe approach around the real you.
                </p>
              </div>
              <div className="flex-shrink-0">
                <Link
                  to="/quiz/food-preferences"
                  className="inline-flex items-center gap-2 bg-forest text-cream px-7 py-4 rounded-full font-body font-medium hover:bg-forest-dark transition-colors"
                  data-testid="flex-detox-foodprefs-cta"
                >
                  Open the Food Picker <ChevronRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </section>


        <section className="max-w-6xl mx-auto px-6 mt-16">
          <div className="mb-6">
            <h2 className="font-heading text-3xl text-forest mb-2">Choose Your Depth</h2>
            <p className="text-stone-600 font-body">
              Three tiers — each builds on the one before. Click <span className="font-medium text-forest">More info</span> on any tier to see exactly what&apos;s included.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tier-grid-preview">
            {tiers.map((tier) => (
              <div
                key={tier.id}
                className="relative text-left rounded-2xl p-5 border-2 border-stone-200 bg-white hover:border-forest/40 hover:shadow-md transition-all"
                data-testid={`tier-card-${tier.slug}`}
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${tier.accent} text-2xl mb-3`}>
                  {tier.icon}
                </div>
                <p className="text-xs font-body text-stone-500 mb-1">Tier {tier.id}</p>
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <h3 className="font-heading text-xl text-forest">{tier.name}</h3>
                  {tier.price && (
                    <span className="font-heading text-lg text-terracotta whitespace-nowrap" data-testid={`tier-price-${tier.slug}`}>{tier.price}</span>
                  )}
                </div>
                <p className={`text-xs font-body ${tier.accentText} font-medium mb-3`}>{tier.tagline}</p>
                <ul className="space-y-1.5 mb-4">
                  {tier.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs font-body text-stone-600">
                      <Check size={12} className="text-forest/70 mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setInfoTier(tier)}
                  data-testid={`tier-info-${tier.slug}`}
                  className="w-full text-xs font-body text-forest border border-forest/30 hover:bg-forest hover:text-cream transition-colors rounded-full py-2 px-3 flex items-center justify-center gap-1"
                >
                  More info <ChevronRight size={12} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* DOUBLE DOWN ADD-ON */}
        <section className="max-w-5xl mx-auto px-6 mt-16" data-testid="flex-detox-double-down-addon">
          <div className="bg-gradient-to-br from-amber-50 to-emerald-50 rounded-3xl border border-stone-200 overflow-hidden">
            <div className="p-8 md:p-10">
              <div className="inline-flex items-center gap-2 bg-forest text-cream rounded-full px-4 py-1.5 mb-4">
                <Sparkles size={15} />
                <span className="text-xs font-body uppercase tracking-[0.15em]">Optional Add-On</span>
              </div>
              <h2 className="font-heading text-3xl text-forest mb-2">Power Up with The Double Down</h2>
              <p className="font-body text-stone-700 leading-relaxed max-w-2xl mb-6">
                A shake up, wake up for your metabolic system. Add <span className="font-medium text-forest">two
                Fascial Maneuver practices per day</span> (Zoom-led or follow-along video) plus cleansing
                recipes, meal plans, and vitality-boosting elixirs to your Flex Detox journey. Not extreme — dedicated.
              </p>

              <div className="grid sm:grid-cols-3 gap-3 mb-6">
                {[
                  { tier: 'The Glow Up', sub: 'Tier 1', price: '+ $49' },
                  { tier: 'Deep Detox', sub: 'Tier 2', price: '+ $29' },
                  { tier: 'Full Immersion', sub: 'Tier 3', price: 'Included free' },
                ].map((row) => (
                  <div key={row.tier} className="bg-white rounded-2xl border border-stone-200 p-5 text-center">
                    <p className="text-xs font-body text-stone-500 mb-1">{row.sub}</p>
                    <p className="font-heading text-lg text-forest mb-2">{row.tier}</p>
                    <p className="font-heading text-2xl text-terracotta">{row.price}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => openInquiry({ tierName: 'Double Down Add-On', source: 'flex-detox-double-down-addon' })}
                  className="inline-flex items-center gap-2 bg-forest text-cream font-body font-medium px-7 py-3 rounded-full hover:bg-forest-dark transition-colors"
                  data-testid="double-down-addon-inquire"
                >
                  Add the Double Down <ChevronRight size={18} />
                </button>
                <Link
                  to="/programs/double-down"
                  className="inline-flex items-center gap-2 bg-white border border-forest/30 text-forest font-body font-medium px-6 py-2.5 rounded-full hover:border-forest transition-colors"
                  data-testid="double-down-addon-learn-more"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHAT'S INSIDE (locked preview) */}
        <section className="max-w-6xl mx-auto px-6 mt-16">
          <div className="bg-white rounded-3xl shadow-card p-8 md:p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-red-200 via-yellow-200 via-green-200 via-blue-200 to-violet-300 mb-5">
              <Lock size={24} className="text-forest" />
            </div>
            <h3 className="font-heading text-2xl md:text-3xl text-forest mb-3">Ready to begin?</h3>
            <p className="text-stone-600 font-body mb-2 max-w-2xl mx-auto">
              The full 21-day journey opens once you&apos;re enrolled — recipes, food lists, system-by-system prep guides, daily affirmations, fascia practice (all tiers), detox protocols + Breathing Room services (Deep Detox & up), and unlimited classes + private sessions (Full Immersion).
            </p>
            <p className="text-stone-500 font-body text-sm mb-6">
              In-person program · Limited spaces · Reach out to find the right tier for you.
            </p>
            <button
              onClick={() => openInquiry({ source: 'flex-detox-locked-cta' })}
              className="inline-flex items-center gap-2 bg-forest text-cream font-body font-medium px-8 py-3 rounded-full hover:bg-forest-dark transition-colors"
              data-testid="flex-detox-inquire-link"
            >
              Inquire about Enrollment <ChevronRight size={18} />
            </button>
          </div>
        </section>

        {/* PROGRAM SCHEDULE & MEETINGS */}
        <section className="max-w-4xl mx-auto px-6 mt-16" data-testid="flex-detox-schedule-section">
          <div className="text-center mb-8">
            <p className="text-xs font-body text-terracotta uppercase tracking-[0.2em] mb-2">Mark Your Calendar</p>
            <h2 className="font-heading text-3xl text-forest">Program Schedule &amp; Meetings</h2>
            <p className="text-stone-600 font-body mt-2 max-w-2xl mx-auto">
              Here&apos;s how we&apos;ll gather throughout the journey. Meetings are planned as follows:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* Info / Intro Sessions */}
            <div className="bg-white rounded-2xl border border-stone-200 p-6" data-testid="flex-detox-intro-meetings">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={18} className="text-terracotta" />
                <h3 className="font-heading text-xl text-forest">Info / Intro Sessions</h3>
              </div>
              <ul className="space-y-3">
                {[
                  { d: 'Wednesday, June 24', t: '1:10 PM' },
                  { d: 'Tuesday, June 30', t: '1:15 PM' },
                ].map((m) => (
                  <li key={m.d} className="flex items-center gap-3">
                    <Calendar size={16} className="text-terracotta flex-shrink-0" />
                    <span className="font-body text-stone-700">
                      {m.d} <span className="text-stone-400">·</span> <span className="font-medium text-forest">{m.t}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-start gap-2 bg-sky-50 border border-sky-100 rounded-xl p-3">
                <Video size={16} className="text-sky-600 flex-shrink-0 mt-0.5" />
                <p className="font-body text-sm text-stone-600">
                  <span className="font-medium text-forest">Optional Zoom meet-up</span> — Saturday, June 27 — for feeling more prepared.
                </p>
              </div>
              <a
                href="https://www.wellnessliving.com/rs/event/breathing_room-rws2se?k_class=981136&k_class_tab=75840"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-forest text-cream text-sm font-body font-medium px-5 py-2.5 rounded-full hover:bg-forest-dark transition-colors"
                data-testid="info-session-signup-link"
              >
                Sign Up for an Info Session — No Commitment <ExternalLink size={14} />
              </a>
            </div>

            {/* Program Meetings */}
            <div className="bg-white rounded-2xl border border-stone-200 p-6" data-testid="flex-detox-program-meetings">
              <div className="flex items-center gap-2 mb-4">
                <Calendar size={18} className="text-forest" />
                <h3 className="font-heading text-xl text-forest">Program Meetings · In Person</h3>
              </div>
              <ul className="space-y-3">
                {['July 8', 'July 15', 'July 22', 'July 29'].map((d) => (
                  <li key={d} className="flex items-center gap-3">
                    <Check size={16} className="text-forest/70 flex-shrink-0" />
                    <span className="font-body text-stone-700">
                      <span className="text-stone-400">Wednesday,</span> <span className="font-medium text-forest">{d}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-start gap-2 bg-sky-50 border border-sky-100 rounded-xl p-3">
                <Video size={16} className="text-sky-600 flex-shrink-0 mt-0.5" />
                <p className="font-body text-sm text-stone-600">
                  A <span className="font-medium text-forest">Zoom alternative</span> will be scheduled if in person isn&apos;t possible.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-5 bg-forest/5 rounded-2xl border border-forest/10 p-6 text-center">
            <p className="font-body text-stone-700 leading-relaxed max-w-2xl mx-auto">
              The program includes <span className="font-medium text-forest">video follow-along</span> you can do anytime — but the in-person meetings can be invaluable for support, info, tips and tricks, and simply hearing how others are navigating the journey.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-6 mt-16" data-testid="flex-detox-faq-section">
          <div className="text-center mb-8">
            <p className="text-xs font-body text-terracotta uppercase tracking-[0.2em] mb-2">Good to Know</p>
            <h2 className="font-heading text-3xl text-forest">Frequently Asked Questions</h2>
          </div>
          <FaqAccordion items={flexDetoxFaqs} testIdPrefix="flex-detox-faq" />
        </section>

        {/* TIER INFO MODAL (same one used in unlocked view) */}
        <AnimatePresence>
          {infoTier && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-forest/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setInfoTier(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-cream rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="tier-info-modal"
              >
                <div className={`bg-gradient-to-br ${infoTier.accent} p-6 md:p-8 flex items-center gap-4`}>
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-white/70 text-3xl flex-shrink-0">
                    {infoTier.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-body text-stone-700 uppercase tracking-wider">Tier {infoTier.id}</p>
                    <h2 className="font-heading text-2xl md:text-3xl text-forest">{infoTier.name}</h2>
                    <p className={`text-sm font-body ${infoTier.accentText} font-medium`}>{infoTier.tagline}</p>
                  </div>
                  <button
                    onClick={() => setInfoTier(null)}
                    className="text-forest/60 hover:text-forest p-2 flex-shrink-0"
                    data-testid="close-tier-info"
                  >
                    ✕
                  </button>
                </div>

                <div className="p-6 md:p-8 space-y-5">
                  {infoTier.summary && infoTier.summary.map((para, i) => (
                    <p key={i} className="font-body text-stone-700 leading-relaxed">
                      {para}
                    </p>
                  ))}

                  <div className="bg-white rounded-xl p-5 border border-stone-200">
                    <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-3">What&apos;s Included</p>
                    <ul className="space-y-2">
                      {infoTier.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 font-body text-sm text-stone-700">
                          <Check size={14} className="text-forest/70 mt-1 flex-shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {infoTier.price && (
                    <div className="bg-forest text-cream rounded-xl p-5" data-testid="tier-pricing">
                      <div className="flex items-end justify-between flex-wrap gap-2">
                        <div>
                          <p className="text-xs font-body uppercase tracking-wider text-cream/70 mb-1">Your Investment</p>
                          <p className="font-heading text-4xl">{infoTier.price}</p>
                        </div>
                        {infoTier.valueNote && (
                          <span className="inline-block bg-cream/15 border border-cream/25 rounded-full px-3 py-1 text-xs font-body text-cream">
                            {infoTier.valueNote}
                          </span>
                        )}
                      </div>
                      {infoTier.priceBlurb && (
                        <p className="font-body text-sm text-cream/85 leading-relaxed mt-3">{infoTier.priceBlurb}</p>
                      )}
                      <p className="font-body text-xs text-cream/75 mt-3 flex items-center gap-1.5">
                        <Check size={13} className="flex-shrink-0" /> Flexible payment options available — just ask.
                      </p>
                    </div>
                  )}

                  <button
                    onClick={() => { const t = infoTier; setInfoTier(null); openInquiry({ tierName: t.name, source: `flex-detox-tier-${t.slug}` }); }}
                    className="w-full block text-center py-3 rounded-full font-body font-medium bg-forest text-cream hover:bg-forest-dark transition-colors"
                    data-testid="inquire-tier"
                  >
                    Inquire about {infoTier.name}
                  </button>

                  <div className="text-center pt-1">
                    <p className="text-xs font-body text-stone-500 mb-2">Not sure yet? Come meet us first.</p>
                    <InfoSessionButton variant="outline" className="w-full" testId="tier-info-session-link" />
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* INQUIRY FORM MODAL */}
        <InquiryFormModal
          key={`inquiry-${inquiry?.openedAt || 'closed'}`}
          isOpen={!!inquiry}
          onClose={() => setInquiry(null)}
          programName="Flex Detox"
          tierName={inquiry?.tierName}
          source={inquiry?.source}
        />
      </div>
    );
  }

  // ============ UNLOCKED VIEW ============
  return (
    <div className="min-h-screen bg-cream pb-20">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-200/40 via-yellow-200/40 via-green-200/40 via-blue-200/40 to-violet-300/40 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-12">
          <Link
            to="/programs"
            className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-6"
            data-testid="flex-detox-back-link"
          >
            <ArrowLeft size={18} /> Back to Programs
          </Link>
          <p className="text-xs font-body text-forest/70 uppercase tracking-[0.25em] mb-3">A 21-Day Holistic Detox</p>
          <h1 className="font-heading text-4xl md:text-6xl text-forest mb-4">The Flex Detox</h1>
          <p className="text-stone-700 font-body text-lg max-w-2xl leading-relaxed">
            Eat with the colors. Move with your body. Choose your depth.
            <br />
            A seven-system nourishment journey through The Root, The Flow, The Fire, The Love, The Truth, The Insight, and The Spirit — designed to make detoxing feel less like a punishment and more like coming home.
          </p>

          {/* System rainbow */}
          <div className="flex items-center gap-1 mt-8" data-testid="system-rainbow">
            {systems.map((c) => (
              <div key={c.id} className="flex-1 group">
                <div className={`h-3 ${c.swatch} rounded-full transition-transform group-hover:scale-y-150 origin-bottom`} />
                <p className="text-[10px] font-body text-forest/60 mt-1 text-center">{c.name}</p>
              </div>
            ))}
          </div>

          {/* Progress */}
          {selectedTier && (
            <div className="mt-8 bg-white/70 backdrop-blur-sm rounded-2xl p-5 border border-white" data-testid="progress-bar">
              <div className="flex items-center justify-between mb-2">
                <p className="font-body text-sm text-forest">
                  Your Journey: <span className="font-medium">{completedCount} of {totalDays} days</span>
                </p>
                <p className="font-body text-sm text-stone-600">{progressPct}%</p>
              </div>
              <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-red-400 via-yellow-400 via-green-400 via-blue-400 to-violet-500 transition-all duration-700"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* INTRO / WELCOME */}
      <section className="max-w-6xl mx-auto px-6 mt-12" data-testid="flex-detox-intro">
        <div className="bg-white rounded-3xl shadow-card border border-stone-100 overflow-hidden">
          <button
            onClick={() => setIntroOpen(!introOpen)}
            className="w-full p-6 md:p-7 flex items-center justify-between gap-4 text-left hover:bg-stone-50 transition-colors"
            data-testid="flex-detox-intro-toggle"
          >
            <div className="min-w-0">
              <p className="text-terracotta font-body text-xs tracking-widest uppercase mb-1">Start Here</p>
              <h2 className="font-heading text-2xl md:text-3xl text-forest">Welcome to The Flex Detox</h2>
              <p className="text-sm font-body text-stone-500 mt-1">Intro video & "Foods for Each System" chart</p>
            </div>
            <ChevronDown
              size={22}
              className={`text-forest flex-shrink-0 transition-transform ${introOpen ? 'rotate-180' : ''}`}
            />
          </button>

          <AnimatePresence>
            {introOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="px-6 md:px-10 pb-8 md:pb-10">
                  <p className="text-stone-700 font-body leading-relaxed max-w-3xl mb-8">
                    This is a gentle, colorful reset built around your body's seven systems. The idea is simple:
                    eat across the rainbow, move in ways that feel good, and choose the depth that fits your life right now.
                    Use the chart below as your everyday guide — each color feeds a different system, from The Root to The Spirit.
                  </p>

                  {/* Intro video placeholder */}
                  <div
                    className="relative rounded-2xl border-2 border-dashed border-stone-200 bg-stone-50 aspect-video flex flex-col items-center justify-center text-center mb-10"
                    data-testid="flex-detox-intro-video"
                  >
                    <PlayCircle size={48} className="text-stone-300 mb-3" />
                    <p className="font-body text-stone-500 text-sm">Intro video coming soon</p>
                    <p className="font-body text-stone-400 text-xs mt-1">Jill will add a welcome video here</p>
                  </div>

                  {/* Foods for each chakra */}
                  <div>
                    <h3 className="font-heading text-2xl text-forest mb-2 text-center">Foods for Each System</h3>
                    <p className="text-stone-500 font-body text-sm text-center mb-6 max-w-2xl mx-auto">
                      A quick-reference guide to the foods that nourish each of the seven systems (chakras).
                    </p>
                    <div className="rounded-2xl overflow-hidden border border-stone-200 bg-white max-w-2xl mx-auto">
                      <img
                        src="https://customer-assets.emergentagent.com/job_1e935ed4-0433-457e-9559-d73f82154bb5/artifacts/vnui4vph_ChakraFoods-3.webp"
                        alt="Foods for each chakra — a guide to nourishing each of the seven body systems"
                        className="w-full h-auto"
                        data-testid="chakra-foods-chart"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* TIER SELECTOR */}
      <section className="max-w-6xl mx-auto px-6 mt-12" data-testid="flex-detox-depth">
        <div className="bg-white rounded-3xl shadow-card border border-stone-100 overflow-hidden">
          <button
            onClick={() => setDepthOpen(!depthOpen)}
            className="w-full p-6 md:p-7 flex items-center justify-between gap-4 text-left hover:bg-stone-50 transition-colors"
            data-testid="flex-detox-depth-toggle"
          >
            <div className="min-w-0">
              <h2 className="font-heading text-2xl md:text-3xl text-forest">Choose Your Depth</h2>
              <p className="text-sm font-body text-stone-500 mt-1">Three tiers + the optional &ldquo;Living in the Full Spectrum&rdquo; bonus week</p>
            </div>
            <ChevronDown size={22} className={`text-forest flex-shrink-0 transition-transform ${depthOpen ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {depthOpen && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                <div className="px-6 md:px-7 pb-7">
                  <p className="text-stone-600 font-body mb-6">
                    Three tiers — each builds on the one before. You can always upgrade along the way.
                  </p>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tier-grid">
          {tiers.map((tier) => {
            const isSelected = selectedTier === tier.id;
            return (
              <div
                key={tier.id}
                className={`relative text-left rounded-2xl p-5 border-2 transition-all bg-white ${
                  isSelected
                    ? 'border-forest shadow-lg scale-[1.02]'
                    : 'border-stone-200 hover:border-forest/40 hover:shadow-md'
                }`}
                data-testid={`tier-card-${tier.slug}`}
              >
                {isSelected && (
                  <div className="absolute -top-3 -right-3 w-8 h-8 bg-forest text-cream rounded-full flex items-center justify-center shadow-md">
                    <Check size={16} />
                  </div>
                )}
                <button
                  onClick={() => saveTier(tier.id)}
                  className="w-full text-left"
                  data-testid={`tier-select-${tier.slug}`}
                >
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${tier.accent} text-2xl mb-3`}>
                    {tier.icon}
                  </div>
                  <p className="text-xs font-body text-stone-500 mb-1">Tier {tier.id}</p>
                  <h3 className="font-heading text-xl text-forest mb-1">{tier.name}</h3>
                  <p className={`text-xs font-body ${tier.accentText} font-medium mb-3`}>{tier.tagline}</p>
                  <ul className="space-y-1.5 mb-4">
                    {tier.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs font-body text-stone-600">
                        <Check size={12} className="text-forest/70 mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setInfoTier(tier); }}
                  data-testid={`tier-info-${tier.slug}`}
                  className="w-full text-xs font-body text-forest border border-forest/30 hover:bg-forest hover:text-cream transition-colors rounded-full py-2 px-3 flex items-center justify-center gap-1"
                >
                  More info <ChevronRight size={12} />
                </button>
              </div>
            );
          })}
        </div>

        {/* Bonus week toggle */}
        <div className="mt-6 bg-white border border-stone-200 rounded-2xl p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="bonus-week-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-300 via-yellow-300 via-green-300 via-blue-300 to-violet-400 flex items-center justify-center flex-shrink-0">
              <Star size={18} className="text-white" />
            </div>
            <div>
              <h3 className="font-heading text-lg text-forest">{bonusWeek.name}</h3>
              <p className="text-xs font-body text-stone-500 mb-1">{bonusWeek.tagline}</p>
              <p className="text-sm font-body text-stone-600">{bonusWeek.description}</p>
            </div>
          </div>
          <button
            onClick={toggleBonus}
            data-testid="toggle-bonus-week"
            className={`flex-shrink-0 px-5 py-2.5 rounded-full font-body font-medium text-sm transition-colors ${
              bonusWeekOn
                ? 'bg-forest text-cream hover:bg-forest-dark'
                : 'bg-stone-100 text-forest hover:bg-stone-200'
            }`}
          >
            {bonusWeekOn ? '✓ Added to Journey' : 'Add to My Journey'}
          </button>
        </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* PREPARATION & OVERVIEW */}
      <section className="max-w-6xl mx-auto px-6 mt-12" data-testid="flex-detox-prep-overview">
        <div className="bg-white rounded-3xl shadow-card border border-stone-100 overflow-hidden">
          <button
            onClick={() => setPrepOpen(!prepOpen)}
            className="w-full p-6 md:p-7 flex items-center justify-between gap-4 text-left hover:bg-stone-50 transition-colors"
            data-testid="flex-detox-prep-toggle"
          >
            <div className="min-w-0">
              <h2 className="font-heading text-2xl md:text-3xl text-forest">Preparation &amp; Overview</h2>
              <p className="text-sm font-body text-stone-500 mt-1">A day&apos;s rhythm + the full Foods for the Detox chart</p>
            </div>
            <ChevronDown size={22} className={`text-forest flex-shrink-0 transition-transform ${prepOpen ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {prepOpen && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                <div className="px-6 md:px-7 pb-7">
                  <p className="text-stone-600 font-body mb-8">
                    Before we begin the color journey, let&apos;s set the foundation — how a day flows, and the foods that
                    support you throughout. Use this as your gentle roadmap. Nothing here is about perfection; it&apos;s about
                    choosing nourishment, one meal at a time.
                  </p>

        {/* A day's rhythm */}
        <div className="grid md:grid-cols-3 gap-4 mb-8" data-testid="detox-daily-rhythm">
          {detoxDailyRhythm.map((block) => (
            <div key={block.part} className="rounded-2xl border border-amber-100 bg-amber-50/60 p-5">
              <h3 className="font-heading text-lg text-forest mb-3">{block.part}</h3>
              <ul className="space-y-2">
                {block.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm font-body text-stone-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-1.5 flex-shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Foods overview table */}
        <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-sm" data-testid="detox-food-overview-table">
          <div className="bg-gradient-to-r from-emerald-100 via-amber-50 to-rose-100 px-6 py-5 text-center border-b border-stone-200">
            <h3 className="font-heading text-2xl text-forest">Foods for the Detox: An Overview</h3>
            <p className="text-sm font-body text-stone-600 mt-1">Your at-a-glance guide to what nourishes — and what to set aside — for these 21 days.</p>
          </div>

          {/* Header row (desktop) */}
          <div className="hidden md:grid grid-cols-[200px_1fr_1fr] bg-stone-50 border-b border-stone-200">
            <div className="px-5 py-3 font-heading text-forest text-sm uppercase tracking-wide">Foods</div>
            <div className="px-5 py-3 font-heading text-emerald-800 text-sm uppercase tracking-wide flex items-center gap-2">
              <Check size={14} className="text-emerald-600" /> Yes to these
            </div>
            <div className="px-5 py-3 font-heading text-rose-800 text-sm uppercase tracking-wide">No to these</div>
          </div>

          {detoxFoodOverview.map((row, i) => (
            <div
              key={row.food}
              className={`md:grid md:grid-cols-[200px_1fr_1fr] border-b border-stone-100 last:border-b-0 ${i % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'}`}
            >
              <div className="px-5 py-3 font-body font-medium text-forest md:border-r md:border-stone-100">
                {row.food}
                {row.food === 'Animal proteins' && (
                  <button
                    type="button"
                    onClick={() => setShowMeatGuide(true)}
                    data-testid="meat-shopping-guide-pill"
                    className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-terracotta/10 hover:bg-terracotta/20 text-terracotta text-xs font-body font-medium px-3 py-1 border border-terracotta/30 transition-colors"
                  >
                    <Sparkles size={12} /> Meat Shopping Guide
                  </button>
                )}
              </div>
              <div className="px-5 py-3 font-body text-sm text-stone-700 bg-emerald-50/50 md:border-r md:border-stone-100">
                <span className="md:hidden text-xs uppercase tracking-wide text-emerald-700 font-medium block mb-1">Yes to these</span>
                {row.yes}
              </div>
              <div className="px-5 py-3 font-body text-sm text-stone-700 bg-rose-50/40">
                <span className="md:hidden text-xs uppercase tracking-wide text-rose-700 font-medium block mb-1">No to these</span>
                {row.no}
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs font-body text-stone-400 italic mt-3">
          A flexible framework, not a rulebook — adapt it to your body, your needs, and any restrictions. When in doubt, choose whole and colorful.
        </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>


      {/* MEAL MAP (21-day, color-coded) */}
      <section className="max-w-6xl mx-auto px-6 mt-12" id="meal-map" data-testid="flex-detox-meal-map">
        <div className="bg-white rounded-3xl shadow-card border border-stone-100 overflow-hidden">
          <button
            onClick={() => setMealMapOpen(!mealMapOpen)}
            className="w-full p-6 md:p-7 flex items-center justify-between gap-4 text-left hover:bg-stone-50 transition-colors"
            data-testid="flex-detox-meal-map-toggle"
          >
            <div className="min-w-0">
              <p className="text-terracotta font-body text-xs tracking-widest uppercase mb-1">Meal Planning</p>
              <h2 className="font-heading text-2xl md:text-3xl text-forest">Your 21-Day Meal Map</h2>
              <p className="text-sm font-body text-stone-500 mt-1">Color-coded by system · drink, breakfast, lunch & dinner ideas with swap options</p>
            </div>
            <ChevronDown size={22} className={`text-forest flex-shrink-0 transition-transform ${mealMapOpen ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {mealMapOpen && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                <div className="px-6 md:px-7 pb-7">
                  <MealMap21 />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>


      {/* CHAKRA JOURNEY */}
      <section className="max-w-6xl mx-auto px-6 mt-16">
        <div className="mb-6">
          <h2 className="font-heading text-3xl text-forest mb-2">Your 21-Day Journey Through the 7 Systems</h2>
          <p className="text-stone-600 font-body">
            Three days per system · seven phases · one full spectrum of nourishment.
          </p>
        </div>

        <div className="space-y-4">
          {systems.map((system) => {
            const isExpanded = expandedSystem === system.id;
            const phaseCompleted = system.days.filter((d) => completedDays[d]).length;
            return (
              <div
                key={system.id}
                className={`bg-gradient-to-br ${system.bg} border ${system.border} rounded-2xl overflow-hidden transition-all`}
                data-testid={`system-phase-${system.id}`}
              >
                <button
                  onClick={() => setExpandedSystem(isExpanded ? null : system.id)}
                  className="w-full p-5 md:p-6 flex items-center justify-between gap-4 text-left hover:bg-white/40 transition-colors"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className={`w-12 h-12 md:w-14 md:h-14 rounded-full ${system.swatch} ring-4 ring-white shadow-md flex-shrink-0`} />
                    <div className="min-w-0">
                      <p className="text-xs font-body text-forest/60 uppercase tracking-wider">
                        Days {system.days[0]}–{system.days[system.days.length - 1]} · {system.endocrineGland}
                      </p>
                      <h3 className="font-heading text-xl md:text-2xl text-forest">{system.name}</h3>
                      <p className="text-sm font-body text-stone-600">{system.coreIssues.join(' · ')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs font-body text-stone-600 hidden sm:inline">
                      {phaseCompleted}/{system.days.length}
                    </span>
                    <ChevronDown
                      size={20}
                      className={`text-forest transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    />
                  </div>
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="px-5 md:px-6 pb-6 space-y-5">
                        {/* Affirmation */}
                        <div className="bg-white/70 rounded-xl p-4">
                          <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-1">Daily Affirmation</p>
                          <p className="font-heading text-lg text-forest italic">&ldquo;{system.affirmation}&rdquo;</p>
                        </div>

                        {/* Enhancements — focal point in the body */}
                        <div className="bg-white/70 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Sparkles size={16} className="text-forest" />
                            <h4 className="font-heading text-lg text-forest">Enhancements</h4>
                          </div>
                          {system.enhancementsIntro && (
                            <p className="text-sm font-body text-stone-700 leading-relaxed mb-4">{system.enhancementsIntro}</p>
                          )}
                          <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-2">
                            Endocrine gland · {system.endocrineGland}
                          </p>
                          <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-2">Anatomy supported</p>
                          <div className="flex flex-wrap gap-1.5 mb-4">
                            {system.anatomy.map((a) => (
                              <span
                                key={a}
                                className={`text-xs font-body px-2.5 py-1 rounded-full border ${system.pill}`}
                              >
                                {a}
                              </span>
                            ))}
                          </div>
                          <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-2">Physiological activities</p>
                          <div className="flex flex-wrap gap-1.5">
                            {system.physiology.map((p) => (
                              <span
                                key={p}
                                className="text-xs font-body px-2.5 py-1 rounded-full border border-stone-200 bg-white text-stone-700"
                              >
                                {p}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Foods */}
                        <div>
                          <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-2">Foods That Nourish This System</p>
                          {system.foodsNarrative && (
                            <p className="text-sm font-body text-stone-700 leading-relaxed mb-3">{system.foodsNarrative}</p>
                          )}
                          <div className="flex flex-wrap gap-2">
                            {system.foods.map((f) => (
                              <span
                                key={f}
                                className={`text-xs font-body px-3 py-1.5 rounded-full border ${system.pill}`}
                              >
                                {f}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Rich food guide (e.g. Crown Chakra foods) */}
                        {system.foodGuide && (
                          <div data-testid={`food-guide-${system.id}`}>
                            <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-2">{system.foodGuide.title}</p>
                            {system.foodGuide.intro && (
                              <p className="text-sm font-body text-stone-700 leading-relaxed mb-3">{system.foodGuide.intro}</p>
                            )}
                            <div className="space-y-3">
                              {system.foodGuide.groups.map((g) => (
                                <div key={g.name} className={`rounded-xl border p-3.5 ${system.border} ${system.bg} bg-gradient-to-br`}>
                                  <p className="font-heading text-forest text-base leading-tight">{g.name}</p>
                                  {g.blurb && <p className="text-xs font-body text-stone-500 italic mb-2 mt-0.5">{g.blurb}</p>}
                                  <ul className="space-y-1">
                                    {g.items.map((it) => (
                                      <li key={it.name} className="text-sm font-body text-stone-700 leading-relaxed">
                                        <span className="font-medium text-forest">{it.name}:</span> {it.note}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Link to the Meal Map */}
                        <a
                          href="#meal-map"
                          className="inline-flex items-center gap-1.5 text-sm font-body font-medium text-terracotta hover:text-terracotta/80 transition-colors"
                          data-testid={`plan-meals-link-${system.id}`}
                        >
                          Plan your meals for {system.name} →
                        </a>

                        {/* Specific food groups */}
                        {system.foodGroups && system.foodGroups.map((group) => (
                          <div key={group.label}>
                            <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-1">{group.label}</p>
                            {group.note && (
                              <p className="text-xs font-body text-stone-500 italic mb-2 leading-relaxed">{group.note}</p>
                            )}
                            <div className="flex flex-wrap gap-2">
                              {group.items.map((item) => (
                                <span
                                  key={item}
                                  className={`text-xs font-body px-3 py-1.5 rounded-full border ${system.pill}`}
                                >
                                  {item}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}

                        {/* Teas */}
                        {system.teas && system.teas.length > 0 && (
                          <div>
                            <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-2">Teas</p>
                            <div className="flex flex-wrap gap-2">
                              {system.teas.map((t) => (
                                <span
                                  key={t}
                                  className={`text-xs font-body px-3 py-1.5 rounded-full border ${system.pill}`}
                                >
                                  {t}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Herbs */}
                        {system.herbs && system.herbs.length > 0 && (
                          <div>
                            <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-2">Herbs</p>
                            <div className="flex flex-wrap gap-2">
                              {system.herbs.map((h) => (
                                <span
                                  key={h}
                                  className={`text-xs font-body px-3 py-1.5 rounded-full border ${system.pill}`}
                                >
                                  {h}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Minerals */}
                        {system.minerals && system.minerals.length > 0 && (
                          <div>
                            <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-2">Minerals</p>
                            <div className="flex flex-wrap gap-2">
                              {system.minerals.map((m) => (
                                <span
                                  key={m}
                                  className={`text-xs font-body px-3 py-1.5 rounded-full border ${system.pill}`}
                                >
                                  {m}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Prepping for this Section */}
                        {system.prep && (
                          <div className="bg-white/70 rounded-xl p-5 border-l-4 border-forest/40" data-testid={`prep-${system.id}`}>
                            <div className="flex items-center gap-2 mb-3">
                              <Sparkles size={16} className="text-forest/70" />
                              <h4 className="font-heading text-lg text-forest">{system.prep.title}</h4>
                            </div>
                            {system.prep.intro && (
                              <p className="font-body text-sm text-stone-700 leading-relaxed mb-4">{system.prep.intro}</p>
                            )}
                            {system.prep.shopping && system.prep.shopping.length > 0 && (
                              <div className="mb-4">
                                <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-2">Gather / Shop For</p>
                                <ul className="space-y-1.5">
                                  {system.prep.shopping.map((s, i) => (
                                    <li key={i} className="flex items-start gap-2 font-body text-sm text-stone-700">
                                      <span className="text-forest/60 mt-1">✦</span>
                                      <span>{s}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {system.prep.mindset && system.prep.mindset.length > 0 && (
                              <div className="mb-4">
                                <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-2">Mind &amp; Mood</p>
                                <ul className="space-y-1.5">
                                  {system.prep.mindset.map((m, i) => (
                                    <li key={i} className="flex items-start gap-2 font-body text-sm text-stone-700">
                                      <span className="text-forest/60 mt-1">✦</span>
                                      <span>{m}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {system.prep.gentle && (
                              <p className="font-body text-sm text-stone-600 italic leading-relaxed border-t border-stone-200 pt-3 mt-2">
                                {system.prep.gentle}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Days */}
                        <div>
                          <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-2">The 3 Days</p>
                          <div className="grid grid-cols-3 gap-3">
                            {system.days.map((dayNum) => {
                              const isComplete = !!completedDays[dayNum];
                              const isFav = !!favoriteDays[dayNum];
                              const practice = dayPractices[dayNum];
                              return (
                                <div
                                  key={dayNum}
                                  className={`group relative rounded-xl border-2 transition-all bg-white flex flex-col ${
                                    isComplete ? 'border-forest' : 'border-stone-200 hover:border-forest/30'
                                  }`}
                                  data-testid={`day-card-wrap-${dayNum}`}
                                >
                                  <button
                                    onClick={() => openDay(system, dayNum)}
                                    data-testid={`day-card-${dayNum}`}
                                    className="text-left p-4 pb-2 flex-1 hover:opacity-90 transition-opacity"
                                  >
                                    <div className="flex items-baseline gap-1.5 mb-1">
                                      <span className="text-xs font-body text-stone-500 uppercase tracking-wide">Day</span>
                                      <span className="font-heading text-2xl text-forest leading-none">{dayNum}</span>
                                      {practice && <Video size={13} className="text-terracotta ml-auto flex-shrink-0" />}
                                    </div>
                                    {practice ? (
                                      <p className="font-body text-[13px] text-stone-700 leading-snug line-clamp-3">{practice.title}</p>
                                    ) : (
                                      <p className="font-body text-[13px] text-stone-300 italic">Practice coming soon</p>
                                    )}
                                  </button>
                                  <div className="flex items-center justify-between border-t border-stone-100 px-3 py-2">
                                    <button
                                      onClick={() => toggleDayComplete(dayNum)}
                                      className="inline-flex items-center gap-1.5 group/chk"
                                      data-testid={`day-complete-${dayNum}`}
                                      title={isComplete ? 'Completed' : 'Mark complete'}
                                    >
                                      <span className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                                        isComplete ? 'bg-forest border-forest text-cream' : 'border-stone-300 group-hover/chk:border-forest'
                                      }`}>
                                        {isComplete && <Check size={11} />}
                                      </span>
                                      <span className="text-[11px] font-body text-stone-500">Done</span>
                                    </button>
                                    <button
                                      onClick={() => toggleFavorite(dayNum)}
                                      className="transition-colors"
                                      data-testid={`day-favorite-${dayNum}`}
                                      title={isFav ? 'Remove favorite' : 'Mark as favorite'}
                                    >
                                      <Heart size={15} className={isFav ? 'text-rose-500 fill-rose-500' : 'text-stone-300 hover:text-rose-400'} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Comments Board */}
                        <FlexCommentsBoard systemId={system.id} systemName={system.name} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}

          {/* Bonus Week Display */}
          {bonusWeekOn && (
            <div className="bg-gradient-to-r from-red-50 via-yellow-50 via-green-50 via-blue-50 to-violet-50 border-2 border-dashed border-violet-300 rounded-2xl p-6" data-testid="bonus-week-section">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-400 via-yellow-400 via-green-400 via-blue-400 to-violet-500 flex items-center justify-center flex-shrink-0">
                  <Star size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-xs font-body text-forest/60 uppercase tracking-wider">Days 22–28 · Bonus</p>
                  <h3 className="font-heading text-2xl text-forest">{bonusWeek.name}</h3>
                  <p className="text-sm font-body text-stone-600">{bonusWeek.tagline}</p>
                </div>
              </div>
              <p className="font-body text-stone-700 mb-4 leading-relaxed">{bonusWeek.description}</p>
              <div className="grid grid-cols-7 gap-2">
                {bonusWeek.days.map((dayNum) => {
                  const isComplete = !!completedDays[dayNum];
                  return (
                    <button
                      key={dayNum}
                      onClick={() => openDay({ name: 'Full Spectrum', endocrineGland: 'Bonus Week', ...bonusWeek }, dayNum)}
                      data-testid={`day-card-${dayNum}`}
                      className={`relative rounded-xl p-3 border-2 bg-white text-center transition-all ${
                        isComplete ? 'border-forest' : 'border-stone-200 hover:border-forest/30'
                      }`}
                    >
                      <p className="text-[10px] font-body text-stone-500">Day</p>
                      <p className="font-heading text-xl text-forest">{dayNum}</p>
                      {isComplete && (
                        <div className="absolute top-1 right-1 w-4 h-4 bg-forest text-cream rounded-full flex items-center justify-center">
                          <Check size={10} />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* REFLECTION & JOURNAL */}
      <ReflectionJournal />

      {/* FINAL REFLECTIONS (after Day 21) */}
      <FinalReflections />

      {/* DAY DETAIL MODAL */}
      <AnimatePresence>
        {selectedDay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-forest/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedDay(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-cream rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="day-detail-modal"
            >
              {/* Header strip */}
              <div
                className="h-3"
                style={{ backgroundColor: selectedDay.system?.hex || '#8a6fbf' }}
              />
              <div className="p-6 md:p-8">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-xs font-body text-stone-500 uppercase tracking-wider">
                      {selectedDay.system.endocrineGland || 'Bonus Week'} · Day {selectedDay.dayNum}
                    </p>
                    <h2 className="font-heading text-3xl text-forest">
                      {selectedDay.system.name}
                    </h2>
                  </div>
                  <button
                    onClick={() => setSelectedDay(null)}
                    className="text-stone-400 hover:text-forest p-2"
                    data-testid="close-day-modal"
                  >
                    ✕
                  </button>
                </div>

                {/* Tier-based content */}
                <div className="space-y-5">
                  {/* Daily Fascia Practice (video) */}
                  {dayPractices[selectedDay.dayNum] && (
                    <div className="bg-white rounded-xl p-5 border border-stone-200" data-testid="day-fascia-practice">
                      <div className="flex items-center gap-2 mb-3">
                        <Video size={16} className="text-terracotta" />
                        <h4 className="font-heading text-lg text-forest">{dayPractices[selectedDay.dayNum].title}</h4>
                      </div>
                      <div className="rounded-xl overflow-hidden border border-stone-200 aspect-video mb-3">
                        <iframe
                          className="w-full h-full"
                          src={`https://www.youtube.com/embed/${dayPractices[selectedDay.dayNum].embedId}`}
                          title={dayPractices[selectedDay.dayNum].title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      </div>
                      <p className="font-body text-sm text-stone-700 leading-relaxed">
                        {dayPractices[selectedDay.dayNum].instruction}
                      </p>
                      {dayPractices[selectedDay.dayNum].affirmations && (
                        <div className="mt-4 rounded-xl bg-terracotta/5 border border-terracotta/15 p-4" data-testid="day-affirmations">
                          <div className="flex items-center gap-2 mb-2">
                            <Sparkles size={14} className="text-terracotta" />
                            <p className="text-xs font-body text-terracotta uppercase tracking-wider">Affirmations</p>
                          </div>
                          <ul className="space-y-1.5">
                            {dayPractices[selectedDay.dayNum].affirmations.map((a, i) => (
                              <li key={i} className="font-body text-sm text-stone-700 italic leading-relaxed">&ldquo;{a}&rdquo;</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Foundations (all tiers) */}
                  {benefits.hasFood && (
                    <div className="bg-white rounded-xl p-5 border border-stone-200">
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles size={16} className="text-amber-500" />
                        <h4 className="font-heading text-lg text-forest">Foundations</h4>
                      </div>
                      {selectedDay.system.affirmation && (
                        <>
                          <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-1">Affirmation</p>
                          <p className="font-body text-stone-700 italic mb-3">&ldquo;{selectedDay.system.affirmation}&rdquo;</p>
                        </>
                      )}
                      {selectedDay.system.coreIssues && (
                        <>
                          <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-1">Core Issues</p>
                          <p className="text-sm font-body text-stone-700 mb-3">{selectedDay.system.coreIssues.join(' · ')}</p>
                        </>
                      )}
                      {selectedDay.system.foods && (
                        <>
                          <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-2">Today&apos;s Foods</p>
                          <div className="flex flex-wrap gap-1.5">
                            {selectedDay.system.foods.map((f) => (
                              <span
                                key={f}
                                className={`text-xs font-body px-2.5 py-1 rounded-full border ${selectedDay.system.pill || 'bg-stone-100 text-stone-700 border-stone-200'}`}
                              >
                                {f}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                      <p className="text-xs font-body text-stone-400 mt-3 italic">Recipes & shopping list coming in the next phase of this build.</p>
                    </div>
                  )}

                  {/* Fascia (tier 2+) */}
                  {benefits.hasFascia && (
                    <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Heart size={16} className="text-emerald-600" />
                        <h4 className="font-heading text-lg text-forest">Fascia Practice</h4>
                      </div>
                      <p className="text-sm font-body text-stone-600">Self-guided fascia maneuvers to support this system&apos;s anatomy and energy.</p>
                      <p className="text-xs font-body text-stone-400 mt-2 italic">Video & sequence coming soon.</p>
                    </div>
                  )}

                  {/* Detox (tier 3+) */}
                  {benefits.hasDetox && (
                    <div className="bg-orange-50 rounded-xl p-5 border border-orange-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Flame size={16} className="text-orange-600" />
                        <h4 className="font-heading text-lg text-forest">Detox Protocol</h4>
                      </div>
                      <p className="text-sm font-body text-stone-600">Targeted elimination support — liver, lymph, bowel, skin.</p>
                      <p className="text-xs font-body text-stone-400 mt-2 italic">Protocol details coming soon.</p>
                    </div>
                  )}

                  {/* BR Services (tier 4+) */}
                  {benefits.hasBR && (
                    <div className="bg-sky-50 rounded-xl p-5 border border-sky-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Droplet size={16} className="text-sky-600" />
                        <h4 className="font-heading text-lg text-forest">Breathing Room Services</h4>
                      </div>
                      <ul className="text-sm font-body text-stone-600 space-y-1">
                        <li>• Weekly AO Scan</li>
                        <li>• Frequency treatments</li>
                        <li>• Weekly Ionic foot detox</li>
                      </ul>
                      <p className="text-xs font-body text-stone-400 mt-2 italic">Booking links coming soon.</p>
                    </div>
                  )}

                  {/* Full Immersion (tier 5) */}
                  {benefits.hasPrivate && (
                    <div className="bg-violet-50 rounded-xl p-5 border border-violet-200">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-base leading-none">✨</span>
                        <h4 className="font-heading text-lg text-forest">Full Immersion</h4>
                      </div>
                      <p className="text-sm font-body text-stone-600">Unlimited classes + 2 private sessions with Jill, scheduled to support your journey.</p>
                    </div>
                  )}

                  {!selectedTier && (
                    <div className="bg-amber-50 rounded-xl p-5 border border-amber-200 text-center">
                      <p className="text-sm font-body text-stone-700">
                        Choose a tier above to see your daily practices.
                      </p>
                    </div>
                  )}

                  {/* Complete button */}
                  <button
                    onClick={() => { toggleDayComplete(selectedDay.dayNum); setSelectedDay(null); }}
                    data-testid="toggle-day-complete"
                    className={`w-full py-3 rounded-full font-body font-medium transition-colors ${
                      completedDays[selectedDay.dayNum]
                        ? 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                        : 'bg-forest text-cream hover:bg-forest-dark'
                    }`}
                  >
                    {completedDays[selectedDay.dayNum] ? 'Mark Day Incomplete' : 'Mark Day Complete'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* TIER INFO MODAL */}
      <AnimatePresence>
        {infoTier && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-forest/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setInfoTier(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-cream rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="tier-info-modal"
            >
              <div className={`bg-gradient-to-br ${infoTier.accent} p-6 md:p-8 flex items-center gap-4`}>
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-white/70 text-3xl flex-shrink-0">
                  {infoTier.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-body text-stone-700 uppercase tracking-wider">Tier {infoTier.id}</p>
                  <h2 className="font-heading text-2xl md:text-3xl text-forest">{infoTier.name}</h2>
                  <p className={`text-sm font-body ${infoTier.accentText} font-medium`}>{infoTier.tagline}</p>
                </div>
                <button
                  onClick={() => setInfoTier(null)}
                  className="text-forest/60 hover:text-forest p-2 flex-shrink-0"
                  data-testid="close-tier-info"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 md:p-8 space-y-5">
                {/* Summary */}
                {infoTier.summary && infoTier.summary.map((para, i) => (
                  <p key={i} className="font-body text-stone-700 leading-relaxed">
                    {para}
                  </p>
                ))}

                {/* Features list */}
                <div className="bg-white rounded-xl p-5 border border-stone-200">
                  <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-3">What&apos;s Included</p>
                  <ul className="space-y-2">
                    {infoTier.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 font-body text-sm text-stone-700">
                        <Check size={14} className="text-forest/70 mt-1 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {infoTier.price && (
                  <div className="bg-forest text-cream rounded-xl p-5" data-testid="tier-pricing">
                    <div className="flex items-end justify-between flex-wrap gap-2">
                      <div>
                        <p className="text-xs font-body uppercase tracking-wider text-cream/70 mb-1">Your Investment</p>
                        <p className="font-heading text-4xl">{infoTier.price}</p>
                      </div>
                      {infoTier.valueNote && (
                        <span className="inline-block bg-cream/15 border border-cream/25 rounded-full px-3 py-1 text-xs font-body text-cream">
                          {infoTier.valueNote}
                        </span>
                      )}
                    </div>
                    {infoTier.priceBlurb && (
                      <p className="font-body text-sm text-cream/85 leading-relaxed mt-3">{infoTier.priceBlurb}</p>
                    )}
                    <p className="font-body text-xs text-cream/75 mt-3 flex items-center gap-1.5">
                      <Check size={13} className="flex-shrink-0" /> Flexible payment options available — just ask.
                    </p>
                  </div>
                )}

                {/* CTA */}
                <button
                  onClick={() => { saveTier(infoTier.id); setInfoTier(null); }}
                  data-testid="select-tier-from-modal"
                  className={`w-full py-3 rounded-full font-body font-medium transition-colors ${
                    selectedTier === infoTier.id
                      ? 'bg-stone-200 text-stone-700'
                      : 'bg-forest text-cream hover:bg-forest-dark'
                  }`}
                >
                  {selectedTier === infoTier.id ? '✓ This is Your Tier' : `Select ${infoTier.name}`}
                </button>

                <div className="text-center pt-1">
                  <p className="text-xs font-body text-stone-500 mb-2">Not sure yet? Come meet us first.</p>
                  <InfoSessionButton variant="outline" className="w-full" testId="tier-info-session-link" />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* INQUIRY FORM MODAL */}
      <InquiryFormModal
        key={`inquiry-${inquiry?.openedAt || 'closed'}`}
        isOpen={!!inquiry}
        onClose={() => setInquiry(null)}
        programName="Flex Detox"
        tierName={inquiry?.tierName}
        source={inquiry?.source}
      />

      {/* MEAT SHOPPING GUIDE MODAL */}
      <AnimatePresence>
        {showMeatGuide && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest/40 backdrop-blur-sm"
            onClick={() => setShowMeatGuide(false)}
            data-testid="meat-guide-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 24, stiffness: 280 }}
              className="bg-cream w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-3xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="meat-guide-modal"
            >
              <div className="sticky top-0 bg-gradient-to-r from-emerald-100 via-amber-50 to-rose-100 px-6 py-5 flex items-start justify-between gap-4 border-b border-stone-200">
                <div>
                  <p className="text-xs font-body text-forest/60 uppercase tracking-wider">Shopping Guide</p>
                  <h3 className="font-heading text-2xl text-forest">{meatGuide.title}</h3>
                </div>
                <button
                  onClick={() => setShowMeatGuide(false)}
                  data-testid="meat-guide-close-btn"
                  className="flex-shrink-0 w-9 h-9 rounded-full bg-white/70 hover:bg-white flex items-center justify-center text-forest transition-colors"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="px-6 py-5 space-y-6">
                <p className="text-sm font-body text-stone-700 italic">{meatGuide.intro}</p>

                {/* Helpful */}
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
                  <h4 className="font-heading text-lg text-emerald-800 flex items-center gap-2 mb-3">
                    <Check size={16} className="text-emerald-600" /> {meatGuide.helpful.title}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {meatGuide.helpful.items.map((item) => (
                      <span key={item} className="inline-flex items-center rounded-full bg-white text-emerald-800 text-xs font-body font-medium px-3 py-1 border border-emerald-200">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Misleading */}
                <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
                  <h4 className="font-heading text-lg text-amber-800 mb-1">{meatGuide.misleading.title}</h4>
                  <p className="text-xs font-body text-amber-700 italic mb-3">{meatGuide.misleading.note}</p>
                  <ul className="space-y-2">
                    {meatGuide.misleading.items.map((it) => (
                      <li key={it.term} className="text-sm font-body text-stone-700">
                        <span className="font-medium text-forest">{it.term}:</span> {it.explanation}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Unhelpful */}
                <div className="rounded-2xl border border-rose-200 bg-rose-50/50 p-4">
                  <h4 className="font-heading text-lg text-rose-800 mb-3">{meatGuide.unhelpful.title}</h4>
                  <ul className="space-y-2">
                    {meatGuide.unhelpful.items.map((it) => (
                      <li key={it.term} className="text-sm font-body text-stone-700">
                        <span className="font-medium text-forest">{it.term}:</span> {it.explanation}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Bermuda Meat Shopping */}
                <div className="rounded-2xl border border-sky-200 bg-sky-50/60 p-4" data-testid="meat-guide-bermuda">
                  <h4 className="font-heading text-lg text-sky-900 mb-2">{meatGuide.bermuda.title}</h4>
                  <p className="text-sm font-body text-stone-700 mb-3">{meatGuide.bermuda.intro}</p>
                  <p className="text-xs font-body font-semibold uppercase tracking-wide text-sky-800/70 mb-2">Best brands available here</p>
                  <ul className="space-y-2 mb-3">
                    {meatGuide.bermuda.brands.map((b) => (
                      <li key={b.name} className="text-sm font-body text-stone-700">
                        <span className="font-medium text-forest">{b.name}</span> — {b.note}
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm font-body text-stone-700 mb-2">{meatGuide.bermuda.extra}</p>
                  <p className="text-sm font-body text-stone-600 italic">{meatGuide.bermuda.closing}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FlexDetoxPage;
